import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import stockService from '../../services/stockService';

const StockExitForm = () => {
  const navigate = useNavigate();
  const { 
    products: contextProducts,
    customers: contextCustomers,
    addStockMovement,
    updateProductStock,
    fetchAllData
  } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  
  const [exitData, setExitData] = useState({
    exitNumber: '',
    date: new Date().toISOString().split('T')[0],
    customerId: '',
    employeeId: '',
    reference: '',
    documentType: 'bon-sortie',
    location: '',
    notes: '',
    items: []
  });
  
  // Utiliser uniquement les données du contexte pour une interconnexion en temps réel
  const customers = contextCustomers || [];
  
  // Utiliser les vrais employés du système
  const { employees: contextEmployees = [] } = useAppData();
  const employees = contextEmployees || [];
  
  // Utiliser les vrais entrepôts du système (warehouses)
  const { warehouses } = useAppData();
  const locations = warehouses && warehouses.length > 0 
    ? warehouses.map(warehouse => ({ id: warehouse.id, name: warehouse.name }))
    : [];
  
  // Utiliser uniquement les produits du contexte, pas de données fictives
  const products = contextProducts || [];

  // Assurer le chargement des données au montage du composant
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Générer un numéro de sortie
  useEffect(() => {
    if (!exitData.exitNumber) {
      const exitNumber = `SOR-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      setExitData(prev => ({ ...prev, exitNumber }));
    }
  }, [exitData.exitNumber]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setExitData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredProducts = products.filter(product => 
    (product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()))) &&
    (product.stock > 0) // Ne montrer que les produits avec du stock
  );

  const handleAddItem = (product) => {
    const newItem = {
      id: product.id,
      name: product.name,
      sku: product.sku || '',
      unit: product.unit || 'Pièce',
      quantity: 1,
      unitPrice: product.price || 0,
      total: product.price || 0,
      maxStock: product.stock || 0
    };
    
    // Check if product already exists in items
    const existingIndex = exitData.items.findIndex(item => item.id === product.id);
    if (existingIndex !== -1) {
      // If exists, increment quantity (but don't exceed stock)
      const updatedItems = [...exitData.items];
      const newQuantity = Math.min(updatedItems[existingIndex].quantity + 1, updatedItems[existingIndex].maxStock);
      updatedItems[existingIndex].quantity = newQuantity;
      updatedItems[existingIndex].total = newQuantity * updatedItems[existingIndex].unitPrice;
      
      setExitData(prev => ({
        ...prev,
        items: updatedItems
      }));
    } else {
      // If new, add to items
      setExitData(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));
    }
    
    setSearchQuery('');
    setShowProductSearch(false);
  };

  const handleRemoveItem = (index) => {
    setExitData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...exitData.items];
    
    if (field === 'quantity') {
      const quantity = Math.min(parseInt(value), updatedItems[index].maxStock);
      updatedItems[index].quantity = quantity;
      updatedItems[index].total = quantity * updatedItems[index].unitPrice;
    } else if (field === 'unitPrice') {
      const unitPrice = parseFloat(value);
      updatedItems[index].unitPrice = unitPrice;
      updatedItems[index].total = updatedItems[index].quantity * unitPrice;
    } else {
      updatedItems[index][field] = value;
    }
    
    setExitData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!exitData.location) {
      toast.error('Veuillez sélectionner un emplacement');
      return;
    }
    
    if (exitData.items.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Créer le mouvement de stock
      const stockMovement = {
        id: exitData.exitNumber,
        date: exitData.date,
        type: 'sortie',
        customerId: exitData.customerId,
        employeeId: exitData.employeeId,
        reference: exitData.reference,
        documentType: exitData.documentType,
        location: exitData.location, // Keep original field for API compatibility
        warehouse: exitData.location, // Also include warehouse property for consistency
        quantity: exitData.items.reduce((total, item) => total + parseInt(item.quantity || 0), 0), // Add total quantity
        productId: exitData.items.length === 1 ? exitData.items[0].id : null, // Set productId if single product
        notes: exitData.notes,
        items: exitData.items,
        totalValue: calculateTotal()
      };
      
      // Process each item using StockService
      console.log('Using StockService for stock exit');
      const processedItems = [];
      const failedItems = [];
      
      for (const item of exitData.items) {
        try {
          // Update product stock using StockService
          await stockService.updateProductStock(item.id, {
            type: 'sortie',
            quantity: item.quantity,
            warehouse: exitData.location,
            reference: exitData.reference,
            notes: exitData.notes || `Sortie de stock: ${exitData.exitNumber}`
          });
          
          processedItems.push(item.id);
          console.log(`Successfully processed item ${item.id}`);
        } catch (itemError) {
          console.error(`Error processing item ${item.id}:`, itemError);
          failedItems.push(item.id);
        }
      }
      
      // Create the overall movement record if needed
      if (addStockMovement && processedItems.length > 0) {
        stockMovement.fromExitForm = true;
        stockMovement.processedItems = processedItems;
        stockMovement.failedItems = failedItems;
        addStockMovement(stockMovement);
      }
      
      // Mettre à jour les données
      fetchAllData();
      
      toast.success('Sortie de stock enregistrée avec succès');
      setIsLoading(false);
      navigate('/stock');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la sortie de stock:', error);
      toast.error('Erreur lors de l\'enregistrement de la sortie de stock');
      setIsLoading(false);
    }
  };

  const calculateTotal = () => {
    return exitData.items.reduce((sum, item) => sum + item.total, 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/stock')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Sortie de Stock
            </h1>
            <p className="text-white/60 mt-1">
              Enregistrez les sorties de produits du stock
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-6">Informations générales</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="exitNumber" className="block text-white font-medium mb-1">
                N° de sortie
              </label>
              <input
                type="text"
                id="exitNumber"
                name="exitNumber"
                value={exitData.exitNumber}
                readOnly
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-white font-medium mb-1">
                Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={exitData.date}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="reason" className="block text-white font-medium mb-1">
                Raison de sortie <span className="text-red-400">*</span>
              </label>
              <select
                id="reason"
                name="reason"
                value={exitData.reason}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {/* exitReasons is removed, so this will be empty or need to be re-added */}
                <option value="sale">Vente</option>
                <option value="transfer">Transfert vers un autre emplacement</option>
                <option value="return">Retour fournisseur</option>
                <option value="damaged">Produits endommagés</option>
                <option value="expired">Produits périmés</option>
                <option value="other">Autre</option>
              </select>
            </div>
            <div>
              <label htmlFor="location" className="block text-white font-medium mb-1">
                Emplacement <span className="text-red-400">*</span>
              </label>
              <select
                id="location"
                name="location"
                value={exitData.location}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- Sélectionner un emplacement --</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* exitData.reason === 'sale' && ( */}
            <div>
              <label htmlFor="customerId" className="block text-white font-medium mb-1">
                Client <span className="text-red-400">*</span>
              </label>
              <select
                id="customerId"
                name="customerId"
                value={exitData.customerId}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={exitData.reason === 'sale'}
              >
                <option value="">-- Sélectionner un client --</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>
            </div>
            {/* ) */}
            
            <div>
              <label htmlFor="employeeId" className="block text-white font-medium mb-1">
                Responsable
              </label>
              <select
                id="employeeId"
                name="employeeId"
                value={exitData.employeeId}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Sélectionner un responsable --</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>{employee.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Document de référence */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-6">Document de référence</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="documentType" className="block text-white font-medium mb-1">
                Type de document
              </label>
              <select
                id="documentType"
                name="documentType"
                value={exitData.documentType}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="bon-livraison">Bon de Livraison</option>
                <option value="facture-vente">Facture de Vente</option>
                <option value="transfert">Bon de Transfert</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label htmlFor="reference" className="block text-white font-medium mb-1">
                N° de référence
              </label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={exitData.reference}
                onChange={handleInputChange}
                placeholder="Ex: BL-2023-001"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Produits */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-white">Produits</h3>
            <button
              type="button"
              onClick={() => setShowProductSearch(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Ajouter un produit</span>
            </button>
          </div>

          {/* Search Product Panel */}
          {showProductSearch && (
            <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center mb-4">
                <h4 className="text-white font-medium">Rechercher un produit</h4>
                <button 
                  type="button"
                  onClick={() => setShowProductSearch(false)}
                  className="ml-auto text-white/60 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou référence..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-3 pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {filteredProducts.length > 0 ? (
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Nom</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Référence</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Stock</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map(product => (
                        <tr key={product.id} className="hover:bg-white/10 transition-colors">
                          <td className="px-4 py-2 text-white">{product.name}</td>
                          <td className="px-4 py-2 text-white/70">{product.sku}</td>
                          <td className="px-4 py-2 text-right text-white/70">{product.stock} {product.unit}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleAddItem(product)}
                              className="px-3 py-1 bg-blue-600/40 text-blue-200 rounded-lg hover:bg-blue-600/60 transition-colors text-xs font-medium"
                            >
                              Ajouter
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-white/60 py-4">Aucun produit en stock trouvé</p>
              )}
            </div>
          )}

          {/* Items Table */}
          {exitData.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Produit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Référence</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Stock dispo.</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Qté</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Prix unitaire</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {exitData.items.map((item, index) => (
                    <tr key={item.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-white">{item.name}</td>
                      <td className="px-4 py-3 text-white/70">{item.sku}</td>
                      <td className="px-4 py-3 text-right text-white/70">{item.maxStock} {item.unit}</td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                          min="1"
                          max={item.maxStock}
                          className="w-20 p-1 bg-white/10 border border-white/20 rounded-lg text-white text-right"
                        />
                        <span className="ml-1 text-white/60">{item.unit}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value))}
                          min="0"
                          step="0.01"
                          className="w-24 p-1 bg-white/10 border border-white/20 rounded-lg text-white text-right"
                        />
                        <span className="ml-1 text-white/60">DA</span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-white">{item.total.toLocaleString()} DA</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-white/5">
                    <td colSpan="5" className="px-4 py-3 text-right font-medium text-white">Total</td>
                    <td className="px-4 py-3 text-right font-medium text-white">{calculateTotal().toLocaleString()} DA</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
              <p className="text-white/50">Aucun produit ajouté</p>
              <button
                type="button"
                onClick={() => setShowProductSearch(true)}
                className="mt-2 px-4 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Ajouter des produits
              </button>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">Notes</h3>
          <textarea
            name="notes"
            id="notes"
            value={exitData.notes}
            onChange={handleInputChange}
            rows="3"
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Notes ou commentaires sur cette sortie de stock..."
          ></textarea>
        </div>

        {/* Boutons de soumission */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/stock')}
            className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl font-semibold hover:from-red-700 hover:to-orange-700 transition-all duration-300 flex items-center space-x-2 disabled:opacity-70"
          >
            {isLoading ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                <span>Enregistrement...</span>
              </>
            ) : (
              <span>Enregistrer</span>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default StockExitForm; 